import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  FilterOptions,
  GamecardPlan,
  GamecardPlanResponse,
  GamecardPlanState,
} from "./types/game-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn } from "./types/types";

const initialState: GamecardPlanState = {
  gamecardPlans: [],
  gamecardPlan: null,
  selectedGamecardPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create GamecardPlan
export const createGamecardPlan = createAsyncThunk<
  GamecardPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/create", async (gamecardPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/game-cards`, gamecardPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type': 'application/json' do not dd this line, allow browser to handle
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create game card plan" },
    );
  }
});

// ✅ Fetch GamecardPlans (Paginated)
export const fetchGamecardPlans = createAsyncThunk<
  GamecardPlanResponse & PaginationReturn,
  any,
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/game-cards`;
    if (urlFilters?.url) {
      urlFilters.url = "";
    }
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card plans" },
    );
  }
});

// ✅ Fetch Single DataPlan by ID
export const fetchGamecardPlanById = createAsyncThunk<
  GamecardPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/game-cards/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card plan" },
    );
  }
});

// ✅ Delete DataPlan
export const deleteGamecardPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/game-cards/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete game card plan" },
    );
  }
});

// ✅ Update DataPlan
export const updateGamecardPlan = createAsyncThunk<
  GamecardPlan,
  { id: number; data: Partial<GamecardPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/game-cards/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update game card plan" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("gamecardPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/game-cards/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

// ✅ Slice
const gamecardPlanSlice = createSlice({
  name: "gamecardPlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetGamecardPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGamecardPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGamecardPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGamecardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.gamecardPlan) {
          state.gamecardPlans.unshift(action.payload.gamecardPlan);
        }
      })
      .addCase(createGamecardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create game card plan";
      })

      // Fetch All
      .addCase(fetchGamecardPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGamecardPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.gamecardPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGamecardPlans.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch game card plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchGamecardPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGamecardPlan = null;
      })
      .addCase(fetchGamecardPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGamecardPlan = action.payload;
      })
      .addCase(fetchGamecardPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch game card plan";
      })

      // Delete
      .addCase(deleteGamecardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGamecardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.gamecardPlans = state.gamecardPlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteGamecardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete game card plan";
      })

      // Update
      .addCase(updateGamecardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGamecardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.gamecardPlans = state.gamecardPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );

        // Update selected data plan if it's the one updated
        if (state.selectedGamecardPlan?.id === action.payload.id) {
          state.selectedGamecardPlan = action.payload;
        }
      })
      .addCase(updateGamecardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update game card plan";
      });
  },
});

export const { setFilters, clearFilters, resetGamecardPlanState } =
  gamecardPlanSlice.actions;
export default gamecardPlanSlice.reducer;
